import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AlertController, LoadingController } from '@ionic/angular';

import { ApiService } from 'src/services/api.service';
import { MessageService } from 'src/services/message.service';

interface CategorySampleChunk {
  chunkContent: string;
  confidence: number;
  articleTitle: string;
  articleId: string;
}

interface CategoryStats {
  totalArticles: number;
  totalChunks: number;
  avgConfidence: number;
  firstAppeared: Date;
  lastUpdated: Date;
}

interface CentroidInfo {
  id: string;
  articleCount: number;
  strength: 'weak' | 'moderate' | 'strong';
  lastUpdated: Date;
}

interface ConfidenceDistribution {
  range: string;
  count: number;
  percentage: number;
}

interface CategoryAnalysisResponse {
  category: string;
  stats: CategoryStats;
  centroidInfo: CentroidInfo | null;
  sampleChunks: CategorySampleChunk[];
  allChunks?: CategorySampleChunk[]; // Optional: all chunks instead of just samples
  confidenceDistribution: ConfidenceDistribution[];
}

@Component({
  selector: 'app-category-analysis',
  templateUrl: './category-analysis.component.html',
  styleUrls: ['./category-analysis.component.scss'],
})
export class CategoryAnalysisComponent implements OnInit {
  categoryId: string | null = null;
  categoryName: string | null = null;
  tenantId: string | null = null;
  dimensionId: string | null = null;
  analysisData: CategoryAnalysisResponse | null = null;
  isLoading = true;
  error: string | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private apiService: ApiService,
    private alertController: AlertController,
    private loadingController: LoadingController,
    private messageService: MessageService,
  ) {}

  ngOnInit() {
    this.route.paramMap.subscribe((params) => {
      this.categoryId = params.get('categoryId');
      this.tenantId = params.get('tenantId');
      this.dimensionId = params.get('dimensionId');
      if (this.categoryId && this.tenantId && this.dimensionId) {
        this.loadAnalysisData();
      } else {
        this.error = 'Missing category ID, tenant ID, or dimension ID';
        this.isLoading = false;
      }
    });
  }

  loadAnalysisData() {
    if (!this.categoryId || !this.tenantId || !this.dimensionId) return;

    this.isLoading = true;
    this.error = null;
    this.analysisData = null;

    const apiUrl = `/knowledge-base/taxonomy/${this.tenantId}/dimensions/${this.dimensionId}/category/${this.categoryId}/analysis`;

    this.apiService.get<CategoryAnalysisResponse>(apiUrl).subscribe({
      next: (response) => {
        this.isLoading = false;
        this.analysisData = response;
      },
      error: (err) => {
        this.isLoading = false;
        if (err.status === 404) {
          this.error = 'Category not found or has no classifications';
        } else {
          this.error = 'Failed to load category analysis: ' + (err.message || 'Unknown error');
        }
      },
    });
  }

  onRefresh() {
    this.loadAnalysisData();
  }

  goBack() {
    // If we have dimensionId, go back to dimension details
    if (this.dimensionId) {
      this.router.navigate(['/super-admin/taxonomy', this.tenantId, this.dimensionId]);
    } else {
      // Fallback to tenant main page
      this.router.navigate(['/super-admin/taxonomy', this.tenantId]);
    }
  }

  getConfidenceColor(confidence: number): string {
    if (confidence >= 0.8) return 'success';
    if (confidence >= 0.6) return 'warning';
    return 'danger';
  }

  getConfidenceText(confidence: number): string {
    if (confidence >= 0.9) return 'Excellent';
    if (confidence >= 0.8) return 'Very Good';
    if (confidence >= 0.7) return 'Good';
    if (confidence >= 0.6) return 'Fair';
    if (confidence >= 0.5) return 'Poor';
    if (confidence >= 0.4) return 'Very Poor';
    return 'Rejected';
  }

  getCentroidStrengthColor(strength: 'weak' | 'moderate' | 'strong'): string {
    switch (strength) {
      case 'strong':
        return 'success';
      case 'moderate':
        return 'warning';
      case 'weak':
        return 'danger';
    }
  }

  getCentroidStrengthText(strength: 'weak' | 'moderate' | 'strong'): string {
    switch (strength) {
      case 'strong':
        return 'Strong (10+ articles)';
      case 'moderate':
        return 'Moderate (3-9 articles)';
      case 'weak':
        return 'Weak (1-2 articles)';
    }
  }

  trackByChunk(index: number, chunk: CategorySampleChunk): string {
    return chunk.articleId + chunk.chunkContent.substring(0, 50);
  }

  trackByDistribution(index: number, item: ConfidenceDistribution): string {
    return item.range;
  }

  truncateContent(content: string, maxLength: number = 150): string {
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength) + '...';
  }

  async showFullContent(content: string) {
    const alert = await this.alertController.create({
      header: 'Full Chunk Content',
      message: content,
      buttons: ['OK'],
      cssClass: 'content-alert',
    });
    await alert.present();
  }

  viewArticleAnalysis(articleId: string) {
    if (!this.tenantId) return;

    // If we have dimensionId, use the dimension-aware route
    if (this.dimensionId) {
      this.router.navigate(['/super-admin/taxonomy', this.tenantId, this.dimensionId, 'article', articleId]);
    } else {
      // Use the old route for backward compatibility (though it will show an error)
      this.router.navigate(['/super-admin/taxonomy', this.tenantId, 'article', articleId]);
    }
  }

  getArticleUrl(articleId: string): string {
    return `/dashboard/knowledge/articles/${articleId}/edit`;
  }

  sortChunksByConfidence() {
    if (!this.analysisData) return;

    const chunks = this.analysisData.allChunks || this.analysisData.sampleChunks;
    chunks.sort((a, b) => b.confidence - a.confidence);
  }

  getDistributionColor(range: string): string {
    if (range.startsWith('0.9-') || range.startsWith('0.8-')) return 'success';
    if (range.startsWith('0.7-') || range.startsWith('0.6-')) return 'warning';
    return 'danger';
  }

  formatDateAge(date: Date): string {
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - new Date(date).getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) return '1 day ago';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return `${Math.floor(diffDays / 365)} years ago`;
  }
}
